import { useTranslation } from 'react-i18next'

import Empty from '~/core/ui/Empty'

import { formatLocationByLocale } from '~/core/ui/utils'
import type {
  FlatResultItem,
  TypeUniversalSearchItem,
  UniversalResult
} from '~/lib/features/universal-search/types'

import UniversalSearchItemView from './USearchItemView'

interface SearchResultsProps {
  results: UniversalResult | undefined
  flatResults: FlatResultItem[]
  selectedIndex: number
  onItemSelect: (item: TypeUniversalSearchItem) => void
  onItemHover: (index: number) => void
  isKeyboardNavigation: boolean
  setIsKeyboardNavigation: (value: boolean) => void
  searchTerm: string
}

const SearchResults = ({
  results,
  flatResults,
  selectedIndex,
  onItemSelect,
  onItemHover,
  isKeyboardNavigation,
  setIsKeyboardNavigation,
  searchTerm
}: SearchResultsProps) => {
  const { t, i18n } = useTranslation()

  if (
    (!results || Object.values(results).every((arr) => !arr.length)) &&
    (searchTerm || '').length > 0
  ) {
    return (
      <div className="flex h-[calc(488px-88px)] items-center justify-center">
        <Empty
          type="empty-search"
          title={`${t('label:uSearch:empty:searchTitle')}`}
          description={`${t('label:uSearch:empty:searchDescription')}`}
        />
      </div>
    )
  }

  if (!results || Object.values(results).every((arr) => !arr.length)) {
    return (
      <div className="flex h-[calc(488px-88px)] items-center justify-center">
        <Empty
          type="empty-data"
          title={`${t('label:uSearch:empty:title')}`}
          description={`${t('label:uSearch:empty:description')}`}
        />
      </div>
    )
  }

  return (
    <div
      className={`modal-universal-search-content h-[calc(488px-88px)] overflow-y-auto scroll-smooth ${
        isKeyboardNavigation ? 'cursor-none' : ''
      }`}>
      {Object.entries(results).map(
        ([key, data]) =>
          data.length > 0 && (
            <div key={key}>
              <div className="h-8 px-3 py-2 text-xs font-medium text-gray-500">{`${t(
                `label:uSearch:title:${key.toLowerCase()}`
              )}`}</div>
              {data.map((item: TypeUniversalSearchItem, index: number) => {
                const globalIndex = flatResults.findIndex((i) => i.id === item.id)
                const iconName =
                  key === 'companies' ? 'Building' : key === 'profiles' ? 'User' : 'Briefcase'
                const label =
                  key === 'companies' ? item.name : key === 'profiles' ? item.full_name : item.title
                const rightText1 =
                  key === 'companies'
                    ? item.company_locations.length
                      ? item.company_locations.length > 1
                        ? `${item.company_locations.length} locations`
                        : formatLocationByLocale(item.company_locations[0], i18n.language).shorted
                      : ''
                    : key === 'profiles'
                      ? item.email
                      : item.job_locations.length
                        ? item.job_locations.length > 1
                          ? `${item.job_locations.length} locations`
                          : formatLocationByLocale(item.job_locations[0], i18n.language).shorted
                        : ''

                const rightText2 = key === 'jobs' ? item.department?.name : ''

                return (
                  <div
                    key={index}
                    id={`search-item-${globalIndex}`}
                    className={`px-3 ${
                      !isKeyboardNavigation ? 'hover:bg-gray-50' : ''
                    } ${globalIndex === selectedIndex ? 'bg-gray-50' : ''}`}
                    onMouseEnter={() => {
                      if (!isKeyboardNavigation) {
                        onItemHover(globalIndex)
                      }
                    }}
                    onMouseMove={() => {
                      if (isKeyboardNavigation) {
                        setIsKeyboardNavigation(false)
                      }
                    }}
                    onClick={() => {
                      onItemSelect(flatResults[globalIndex])
                    }}>
                    <UniversalSearchItemView
                      selected={globalIndex === selectedIndex}
                      rightText1={rightText1 || ''}
                      rightText2={rightText2 || ''}
                      label={label || ''}
                      iconName={iconName}
                      type={key}
                    />
                  </div>
                )
              })}
            </div>
          )
      )}
    </div>
  )
}

export default SearchResults
